/**
 * SupplierSelectionPage Component
 * 협력사 선택 페이지 (본사 사용자용)
 *
 * 기능:
 * - 협력사 목록 조회 및 필터링
 * - 등급 엑셀 파일 업로드
 * - 협력사 선택 시 상세 페이지 이동
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { usePagination } from '@/shared/hooks/usePagination';
import { ITEMS_PER_PAGE } from '../constants/supplierStatus.constants';
import { useSupplierSelectionHandlers } from '../hooks/useSupplierSelectionHandlers';
import FilterSection from './FilterSection';
import SupplierTable from './SupplierTable';
import { SupplierDetail, SortConfig } from '../types/supplier.types';
import Loading from '@/shared/components/feedback/Loading';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import '@/shared/styles/common-table.css';

interface SupplierSelectionPageProps {
    onSupplierSelect: (supplierId: string, supplierName: string) => void;
    pageTitle?: string;
}

const SupplierSelectionPage: React.FC<SupplierSelectionPageProps> = ({
                                                                         onSupplierSelect,
                                                                         pageTitle = '협력사 현황'
                                                                     }) => {
    // ========================================
    // 1. Hooks - 페이지네이션 (초기값)
    // ========================================
    const {
        resetPagination
    } = usePagination({ items: [], itemsPerPage: ITEMS_PER_PAGE.LARGE });

    // ========================================
    // 2. Hooks - 핸들러
    // ========================================
    const {
        suppliers,
        loading,
        uploading,
        error,
        filters,
        fileInputRef,
        setFilters,
        fetchSuppliers,
        handleSearch,
        handleQuickFilter,
        handleKeyDown,
        handleRowClick,
        handleUploadClick,
        handleFileChange,
        handleRetry,
        handleExcelDownload
    } = useSupplierSelectionHandlers({
        onSupplierSelect,
        resetPagination
    });

    // ========================================
    // 3. State - 정렬 상태
    // ========================================
    const [sortConfig, setSortConfig] = useState<SortConfig>({
        columnKey: null,
        direction: null,
    });

    /**
     * 정렬 핸들러
     */
    const handleSort = useCallback((columnKey: string) => {
        setSortConfig((prev) => {
            if (prev.columnKey === columnKey) {
                if (prev.direction === 'asc') return { columnKey, direction: 'desc' };
                if (prev.direction === 'desc') return { columnKey: null, direction: null };
            }
            return { columnKey, direction: 'asc' };
        });
    }, []);

    /**
     * 등급 정렬 순서 (마스터 > 다이아 > 골드 > 실버 > 브론즈 > 등급없음)
     */
    const GRADE_ORDER: Record<string, number> = {
        '마스터': 1,
        '다이아': 2,
        '골드': 3,
        '실버': 4,
        '브론즈': 5,
        '등급없음': 6,
        '-': 7
    };

    /**
     * 운영상태 정렬 순서 (운영중 > 대기중 > 일시정지 > 운영중지요청 > 운영종료)
     */
    const STATUS_ORDER: Record<string, number> = {
        '운영중': 1,
        '대기중': 2,
        '일시정지': 3,
        '운영중지요청': 4,
        '운영종료': 5,
        '-': 6
    };

    /**
     * 정렬된 전체 데이터
     */
    const sortedSuppliers = useMemo(() => {
        if (!suppliers || !sortConfig.columnKey || !sortConfig.direction) {
            return suppliers || [];
        }

        return [...suppliers].sort((a, b) => {
            const key = sortConfig.columnKey as keyof SupplierDetail;
            const aVal = a[key];
            const bVal = b[key];

            // null/undefined/'-' 처리
            const aEmpty = aVal == null || aVal === '-' || aVal === '';
            const bEmpty = bVal == null || bVal === '-' || bVal === '';

            if (aEmpty && bEmpty) return 0;
            if (aEmpty) return sortConfig.direction === 'asc' ? 1 : -1;
            if (bEmpty) return sortConfig.direction === 'asc' ? -1 : 1;

            // 등급 컬럼 특수 정렬
            if (key === 'grade') {
                const aOrder = GRADE_ORDER[String(aVal)] || 99;
                const bOrder = GRADE_ORDER[String(bVal)] || 99;
                return sortConfig.direction === 'asc'
                    ? aOrder - bOrder
                    : bOrder - aOrder;
            }

            // 운영상태 컬럼 특수 정렬
            if (key === 'operationStatus') {
                const aOrder = STATUS_ORDER[String(aVal)] || 99;
                const bOrder = STATUS_ORDER[String(bVal)] || 99;
                return sortConfig.direction === 'asc'
                    ? aOrder - bOrder
                    : bOrder - aOrder;
            }

            // 날짜 문자열 비교 (YYYY-MM-DD 또는 YYYY.MM.DD 형식)
            const aStr = String(aVal);
            const bStr = String(bVal);
            return sortConfig.direction === 'asc'
                ? aStr.localeCompare(bStr)
                : bStr.localeCompare(aStr);
        });
    }, [suppliers, sortConfig]);

    // ========================================
    // 4. Hooks - 페이지네이션 (정렬된 데이터 기준)
    // ========================================
    const paginationResult = usePagination({
        items: sortedSuppliers,
        itemsPerPage: ITEMS_PER_PAGE.LARGE
    });

    // ========================================
    // 5. Side Effects - 초기 데이터 로드
    // ========================================
    useEffect(() => {
        fetchSuppliers();
    }, [fetchSuppliers]);

    // ========================================
    // 5. Render - 로딩 상태
    // ========================================
    if (loading || uploading) {
        return (
            <Loading
                message={uploading
                    ? "협력사 등급을 업로드하고 있습니다..."
                    : "협력사 목록을 불러오는 중..."
                }
            />
        );
    }

    // ========================================
    // 6. Render - 에러 상태 (API 호출 실패)
    // ========================================
    if (error) {
        return (
            <div className="supplier-selection-page">
                <div className="page-title-wrapper">
                    <h1 className="page-title">{pageTitle}</h1>
                </div>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                    backgroundColor: 'var(--color-white)',
                    border: '1px solid var(--color-gray-200)',
                    borderRadius: 'var(--border-radius-lg)',
                    padding: '40px'
                }}>
                    <i className="fas fa-exclamation-triangle" style={{
                        fontSize: '3rem',
                        color: 'var(--color-gray-400)',
                        marginBottom: '20px'
                    }}></i>
                    <p style={{
                        fontSize: '1.1rem',
                        color: 'var(--color-gray-600)',
                        marginBottom: '20px',
                        textAlign: 'center'
                    }}>
                        협력사 목록을 불러오는데 실패했습니다.<br />
                        네트워크 연결을 확인하고 다시 시도해주세요.
                    </p>
                    <button
                        className="search-btn"
                        onClick={handleRetry}
                        style={{ marginLeft: 0 }}
                    >
                        <i className="fas fa-redo"></i>
                        <span>다시 시도</span>
                    </button>
                </div>
            </div>
        )
    }

    // ========================================
    // 7. Render - 메인 UI
    // ========================================
    return (
        <div className="supplier-selection-page">
            {/* 페이지 제목 */}
            <div className="page-title-wrapper">
                <h1 className="page-title">{pageTitle}</h1>
            </div>

            {/* 숨겨진 파일 입력 */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />

            {/* 필터 섹션 */}
            <FilterSection
                filters={filters}
                onFilterChange={setFilters}
                onSearch={handleSearch}
                onQuickFilter={handleQuickFilter}
                onKeyDown={handleKeyDown}
                onUploadClick={handleUploadClick}
                onExcelDownload={handleExcelDownload}
            />

            {/* 검색 결과 건수 */}
            {sortedSuppliers.length > 0 && (
                <div className="result-count">
                    <i className="fas fa-list"></i>
                    총 <strong>{sortedSuppliers.length.toLocaleString()}개</strong> 협력사
                </div>
            )}

            {/* 테이블 섹션 */}
            <SupplierTable
                suppliers={paginationResult.currentItems}
                loading={loading}
                onRowClick={handleRowClick}
                sortConfig={sortConfig}
                onSort={handleSort}
                startIndex={(paginationResult.currentPage - 1) * ITEMS_PER_PAGE.LARGE}
            />

            {/* 페이지네이션 */}
            <Pagination
                currentPage={paginationResult.currentPage}
                totalPages={paginationResult.totalPages}
                onPageChange={paginationResult.goToPage}
            />
        </div>
    );
};

export default SupplierSelectionPage;
